// src/App.js
import React, { useContext } from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

import AuthPage from "./pages/AuthPage";
import Dashboard from "./pages/Dashboard";
import Leaderboard from "./pages/Leaderboard";
import Fixtures from "./pages/Fixtures";
import PredictionForm from "./pages/PredictionForm";
import TablePredictions from "./pages/TablePredictions";
import Profile from "./pages/Profile";
import Settings from "./pages/Settings";
import AdminDashboard from "./pages/AdminDashboard";
import SeasonRecap from "./pages/SeasonRecap";
import TeamPage from "./pages/Team";
import VerifyOrResend from "./pages/VerifyOrResend";

import About from "./pages/About";
import Contact from "./pages/Contact";
import Terms from "./pages/Terms";
import Privacy from "./pages/Privacy";
import ForgotPassword from "./pages/ForgotPassword";
import ResetPassword from "./pages/ResetPassword";

import Navbar from "./components/Navbar";
import BottomNav from "./components/BottomNav"; // Import BottomNav
import Footer from "./components/Footer";
import ScrollToTop from "./components/ScrollToTop";

import { UserContext } from "./context/UserContext";
import LoadingSpinner from "./components/LoadingSpinner";

function AppContent() {
  const { user } = useContext(UserContext);
  const location = useLocation();
  const authed = !!user;
  
  // Hide navbar and footer on auth-related pages
  const isAuthPage = location.pathname === "/auth" || 
                     location.pathname === "/forgot-password" || 
                     location.pathname === "/reset-password" ||
                     location.pathname === "/verify-email";
  const showNavbar = !isAuthPage;
  const showFooter = !isAuthPage;

  return (
    <div className="flex flex-col min-h-screen">
      <ScrollToTop />
      {showNavbar && <Navbar />}

      <main className={`flex-grow ${authed && !isAuthPage ? "pb-nav" : ""}`}>
        <Routes>
          {/* Root: smart redirect based on valid JWT */}
          <Route
            path="/"
            element={
              authed ? <Navigate to="/dashboard" replace /> : <Navigate to="/auth" replace />
            }
          />

          {/* Auth - /auth should show AuthPage unless JWT is valid */}
          <Route
            path="/auth"
            element={
              !authed ? <AuthPage /> : <Navigate to="/dashboard" replace />
            }
          />

          {/* Static */}
          <Route path="/about"   element={<About />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms"   element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />

          {/* Public auth helpers */}
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyOrResend />} />

          {/* Protected */}
          <Route
            path="/dashboard"
            element={authed ? <Dashboard /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/leaderboard"
            element={authed ? <Leaderboard /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/fixtures"
            element={authed ? <Fixtures /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/predictions"
            element={authed ? <PredictionForm user={user} /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/table-predictions"
            element={
              authed ? (
                user?.has_table_prediction_access || user?.is_admin ? (
                  <TablePredictions />
                ) : (
                  <Navigate to="/dashboard" replace />
                )
              ) : (
                <Navigate to="/auth" replace />
              )
            }
          />
          <Route
            path="/team"
            element={authed ? <TeamPage /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/profile"
            element={authed ? <Profile /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/settings"
            element={authed ? <Settings /> : <Navigate to="/auth" replace />}
          />
          <Route
            path="/season/:seasonId/recap"
            element={authed ? <SeasonRecap /> : <Navigate to="/auth" replace />}
          />

          {/* Admin-only (only include when authed) */}
          {authed && user?.is_admin && (
            <Route path="/admin" element={<AdminDashboard />} />
          )}

          {/* Catch-all */}
          <Route
            path="*"
            element={
              authed ? <Navigate to="/dashboard" replace /> : <Navigate to="/auth" replace />
            }
          />
        </Routes>
      </main>

      {/* Bottom Navigation (Mobile Only) */}
      {authed && !isAuthPage && <BottomNav />}

      {showFooter && <div className="hidden md:block"><Footer /></div>} {/* Hide large footer on mobile to save space */}
    </div>
  );
}

function App() {
  const { loading } = useContext(UserContext);

  if (loading) {
    return <LoadingSpinner message="Loading..." />;
  }

  return <AppContent />;
}

export default App;
